import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EProcessStatus, IProcessDto } from 'core/dto';
import { pairBehaviorSubjects, subjectFromObs } from 'core/rx-js';
import { ScreenUtilsService } from 'core/services';
import { BehaviorSubject, map, Subject } from 'rxjs';
import { designerPaths } from '../../../designer.paths';

import { PageBaseDirective } from '../../../shared-components/page-base.directive';
import { ProcessReviewComponent } from '../../sign-process-wizard/steps/4-review-step/process-review/process-review.component';
import { ProcessActionsService } from '../process-action-template/process-actions.service';

@Component({
  selector: 'ss-process-overview-detail',
  templateUrl: './process-overview.detail.component.html',
  styleUrls: ['./process-overview.detail.component.scss'],
  host: {
    class: 'display-block full-height'
  },
  hostDirectives: [PageBaseDirective],
  providers: [ProcessActionsService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessOverviewDetailComponent {

  readonly #destroy = new Subject<void>()

  @ViewChild(ProcessReviewComponent)
  private _processReviewComponent: ProcessReviewComponent

  readonly isSmallerScreenObs = this._screenUtilsService.isSmallerScreenObs;
  readonly processIdSubject = new BehaviorSubject<string>(undefined);
  readonly processSubject = new BehaviorSubject<IProcessDto>(undefined);

  readonly canCancelSubject = this._processActionsService.canCancelSubject;
  readonly canSignSubject = this._processActionsService.canSignSubject;
  readonly canDeleteSubject = this._processActionsService.canDeleteSubject;
  readonly canRejectSubject = this._processActionsService.canRejectSubject;
  readonly noMenuActionsSubject = this._processActionsService.noMenuActionsSubject;
  readonly canDownloadAuditTrailSubject = this._processActionsService.canDownloadAuditTrailSubject;
  readonly canDownloadSubject = this._processActionsService.canDownloadSubject;
  readonly canRestartSubject = this._processActionsService.canRestartSubject;
  readonly canChangeValidTillSubject = this._processActionsService.canChangeValidTillSubject;

  readonly processActionKeys = [
    'process.cancel',
    'activity.reject',
    'process.delete',
    'process.download-audit-trail',
    'process.download-documents',
    'process.change-valid-till'
  ]

  readonly loaderKeys = [
    'process.load-one'
  ]

  readonly loaderKeysSubject = subjectFromObs({
    observable: this.processIdSubject.pipe(
      map((processId) => {
        if (!processId) {
          return this.loaderKeys
        }

        return [
          ...this.loaderKeys,
          ...this.processActionKeys.map((key) => [key, processId].join('.'))
        ]
      }),

    ),
    initialValue: this.loaderKeys,
    until: this.#destroy
  })

  readonly smallScreenSubject = this._screenUtilsService.screenSizeSubject.pipe(
    map((size) => size === 'sm' || size === 'xs')
  )

  @ViewChild('cardHeader', { read: ElementRef })
  private _header: ElementRef<HTMLElement>

  get header() {
    return this._header?.nativeElement
  }

  processStatus: EProcessStatus

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _processActionsService: ProcessActionsService,
    private readonly _screenUtilsService: ScreenUtilsService
  ) {

    this.processIdSubject.next(this._activatedRoute.snapshot.params.processId);
    this.processStatus = this._activatedRoute.snapshot.queryParams.processStatus;

  }

  ngAfterViewInit() {
    pairBehaviorSubjects(this._processReviewComponent.processSubject, this.processSubject, this._processActionsService.processSubject)
  }

  reject() {

    this._processActionsService.reject();
  }

  cancel() {
    this._processActionsService.cancel();
  }

  deleteProcess() {
    this._processActionsService.deleteProcess();

  }

  downloadDocuments() {
    this._processActionsService.downloadDocuments();

  }

  downloadAuditTrail() {

    this._processActionsService.downloadAuditTrail();
  }

  restart() {
    this._processActionsService.restart();
  }

  sign() {
    this._processActionsService.sign()
  }

  changeValidTill() {
    this._processActionsService.changeValidTill();
  }


  back() {
    if (this.processStatus) {
      this._router.navigate([designerPaths.processOverview, this.processStatus]);
      return;
    }

    this._router.navigate([designerPaths.processOverview]);
  }




  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }
}
